import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Static legal page. Content sourced from the official Vehimax
 * "Condiciones de uso" document (Spanish).
 *
 * If the legal text is updated, edit the JSX below.
 */
export default function TermsOfServicePage() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-secondary" data-testid="terms-of-service-page">
      {/* Page header */}
      <div className="bg-primary text-white">
        <div className="max-w-[900px] mx-auto px-4 md:px-8 py-12">
          <Link to="/">
            <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 -ml-3 mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al inicio
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <FileText className="h-8 w-8 text-accent" />
            <span className="text-accent font-heading text-sm font-bold uppercase tracking-widest">
              Documento legal
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight">
            Términos de servicio
          </h1>
          <p className="text-white/70 mt-3">Condiciones de uso de Vehimax</p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[900px] mx-auto px-4 md:px-8 py-12">
        <article className="bg-white border border-border rounded-md p-6 md:p-10 prose-vehimax">
          <p className="lead text-lg leading-relaxed text-foreground mb-4">
            Estos Términos de uso (estos &ldquo;Términos&rdquo;), junto con nuestra{' '}
            <Link to="/privacy" className="text-accent hover:underline">
              política de privacidad
            </Link>
            , rigen su acceso y uso de este sitio web (Vehimax.com), incluyendo todos los servicios
            que ofrecemos actualmente o que podamos ofrecer en el futuro a través de nuestro Sitio.
            Nuestro Sitio y Servicios son proporcionados por DAVID 400 LLC. (&ldquo;Vehimax&rdquo;,
            &ldquo;nosotros&rdquo;, &ldquo;nuestro&rdquo; o &ldquo;nos&rdquo;). &ldquo;Usted&rdquo;
            o &ldquo;su&rdquo; se refiere al usuario que accede y utiliza nuestro Sitio y
            Servicios, y puede referirse a una persona física o jurídica.
          </p>

          <p className="font-semibold text-foreground my-4">
            ESTOS TÉRMINOS CONSTITUYEN UN CONTRATO VINCULANTE ENTRE USTED Y Vehimax. POR FAVOR,
            LÉALOS DETENIDAMENTE.
          </p>

          <p className="text-muted-foreground leading-relaxed mb-6">
            Al acceder y utilizar nuestro sitio web y servicios, usted confirma que ha leído,
            comprendido y acepta estar sujeto a estos términos. Le recomendamos que imprima una
            copia de estos términos para futuras consultas.
          </p>

          <Section title="1. Servicios en general">
            <p>
              Ofrecemos plataformas que permiten a los usuarios publicar artículos para la venta
              (&ldquo;Artículos Publicados&rdquo;) y que otros usuarios los vean, pero no somos ni
              vendedores ni compradores de dichos artículos. Los usuarios que decidan utilizar
              nuestros Servicios actúan bajo su propia responsabilidad y son responsables de las
              acciones que realicen en relación con las transacciones que lleven a cabo. No
              representamos a ningún usuario de nuestro Sitio o Servicios, ni participamos en
              ninguna transacción entre ellos. Usted es el único responsable de resolver cualquier
              disputa que tenga con otros usuarios de nuestro Sitio o Servicios. Sin embargo,
              podemos, a nuestra entera discreción, supervisar o investigar disputas o problemas
              entre usuarios para proteger nuestro Sitio y Servicios y abordar posibles
              incumplimientos de estos Términos por parte de un usuario.
            </p>
            <p>
              Podemos utilizar inteligencia artificial o herramientas y productos basados ​​en IA
              para proporcionar y mejorar nuestros Servicios, ofrecerle una experiencia
              personalizada, brindarle un mejor servicio al cliente y dar soporte a nuestro Sitio y
              Servicios; no se garantiza la disponibilidad ni la precisión de estas herramientas.
            </p>
            <p>
              No nos dedicamos a la compraventa de ningún artículo anunciado a comisión. No
              aceptamos ningún artículo anunciado en consignación. Todas las transacciones y los
              términos de cualquier acuerdo que pueda surgir entre los usuarios de nuestro sitio
              web o servicios se determinan exclusivamente mediante negociación e interacción
              directa entre usuarios. No actuamos como empleados, agentes, representantes,
              corredores, distribuidores, agentes inmobiliarios, agentes asociados, agentes de
              bienes raíces, agentes hipotecarios, prestamistas hipotecarios, tasadores de bienes
              raíces, agencias de marketing ni agentes de marketing de ningún usuario de nuestro
              sitio web o servicios. No producimos, fabricamos, manipulamos, conservamos,
              mantenemos, almacenamos, procesamos, distribuimos, transportamos, entregamos ni
              tomamos posesión de ningún artículo anunciado en ningún momento. No contamos con
              fianza para la protección de vendedores, compradores ni de ningún usuario de nuestro
              sitio web o servicios.
            </p>
          </Section>

          <Section title="2. Acceso y uso">
            <p>
              Si bien nos esforzamos por brindarle nuestro Sitio y Servicios siempre que cumpla con
              estos Términos, no garantizamos su seguridad ni que estén libres de errores o virus.
              Usted es responsable de configurar su tecnología de la información, programas
              informáticos y plataformas para acceder o utilizar nuestro Sitio y Servicios. Debe
              utilizar su propio software antivirus.
            </p>
            <p>
              Solo podrá utilizar nuestro Sitio y Servicios si puede celebrar un contrato
              vinculante con nosotros y en cumplimiento de estos Términos y de todas las leyes,
              normas y reglamentos locales, estatales, nacionales e internacionales aplicables.
              Queda estrictamente prohibido el uso de nuestros Sitios o Servicios por parte de
              cualquier persona menor de edad en su estado de residencia. Queda estrictamente
              prohibido el uso de nuestros Sitios o Servicios para contactar a otros usuarios con
              fines de marketing, venta o promoción de cualquier producto o servicio, que no sean
              los Artículos Listados, o para acosar a otros usuarios.
            </p>
            <p>
              Nuestro Sitio y Servicios no están disponibles para ningún usuario o visitante que
              hayamos eliminado, suspendido o bloqueado previamente de nuestro Sitio y/o Servicios.
              Podemos suspender, cancelar o bloquear su acceso a nuestro Sitio y/o Servicios en
              cualquier momento, por cualquier motivo (como su incumplimiento grave de estos
              Términos, nuestra creencia razonable de que es necesario para proteger la seguridad o
              la propiedad de otros, para prevenir el fraude o para fines de evaluación de riesgos)
              o sin motivo alguno. Nos reservamos el derecho de limitar su uso de nuestro Sitio o
              Servicios o de bloquear o cancelar su cuenta de inmediato, sin previo aviso ni
              reembolso de las cantidades que nos haya pagado.
            </p>
            <p>
              Al registrar una cuenta de usuario con nosotros y/o usar, acceder o visitar nuestro
              Sitio y Servicios, usted declara y garantiza que:
            </p>
            <ol className="list-[lower-alpha] ml-6 space-y-2 marker:text-accent marker:font-semibold">
              <li>cumplirá con todos los términos establecidos en el presente documento;</li>
              <li>
                no proporcionará información falsa, engañosa, fraudulenta, ficticia o confusa sobre
                usted o el/los Artículo(s) Listado(s);
              </li>
              <li>
                no publicará ninguna información en nuestro Sitio o Servicios que sea ofensiva,
                grosera, despectiva, destinada a engañar o embaucar a otros usuarios/visitantes,
                amenazante, abusiva, acosadora, odiosa, difamatoria, pornográfica, política,
                racista o de otro modo ilegal u ofensiva, o que no se relacione generalmente con el
                tema o asunto designado;
              </li>
              <li>
                mantendrá la seguridad de cualquier código de identificación de usuario y/o
                contraseña asociada con su cuenta, nos notificará inmediatamente si sospecha que
                dicha seguridad se ha visto comprometida y acepta todos los riesgos de acceso
                autorizado y no autorizado a su cuenta y la información que nos proporciona;
              </li>
              <li>
                no violará los derechos de propiedad intelectual o privacidad de ningún tercero;
              </li>
              <li>
                no utilizará nuestro Sitio o Servicios para contactar a otros usuarios con el
                propósito de comercializar, vender o promocionar cualquier producto o servicio, que
                no sean los Artículos Listados;
              </li>
              <li>
                no utilizará nuestro Sitio o Servicios para acosar a otros usuarios de nuestro
                Sitio o Servicios;
              </li>
              <li>
                acepta y consiente que todas las comunicaciones y llamadas telefónicas que Vehimax
                reciba, procese o reenvíe desde, hacia o entre usted, otros usuarios, vendedores
                y/o compradores a través de nuestro sistema de telecomunicaciones, nuestro Sitio o
                nuestros Servicios pueden ser grabadas, transcritas, resumidas, analizadas,
                monitoreadas y/o almacenadas por Vehimax a través de cualquier método o tecnología,
                incluidas herramientas de inteligencia artificial (&ldquo;IA&rdquo;), para garantía
                de calidad; para proporcionarnos clientes potenciales de marketing/ventas a
                nosotros, a nuestros afiliados o a empresas de terceros; para proporcionarle
                Servicios; y cumplimiento de estos Términos; e
              </li>
              <li>
                acepta y asume la responsabilidad total por los Artículos Listados ofrecidos y la
                precisión del contenido del listado, incluido el contenido del listado creado que
                puede hacer uso de herramientas de inteligencia artificial generativa.
              </li>
            </ol>
            <p>
              Si usted registra una cuenta de usuario o utiliza nuestro Sitio y Servicios en nombre
              de un tercero (como otra persona física o jurídica), usted declara y garantiza que es
              un representante autorizado de dicha persona o entidad con autoridad para vincularla
              a estos Términos, y acepta estos Términos en nombre de dicha persona o entidad.
            </p>
            <p>
              Las cuentas de usuario son intransferibles. Solo usted puede usar su cuenta de
              usuario. Usted es responsable de todas las actividades que se realicen a través de su
              cuenta de usuario.
            </p>
            <p>
              Podemos verificar su cuenta recopilando su información de contacto (incluyendo, entre
              otros, el nombre legal de la empresa, la dirección postal y el número de
              identificación fiscal) e información bancaria, llamándolo directamente para confirmar
              la exactitud de la información de su cuenta y tomando cualquier otra medida que
              consideremos necesaria.
            </p>
          </Section>

          <Section title="3. Derechos de propiedad intelectual">
            <p>
              Somos los propietarios o licenciatarios de todos los derechos de propiedad
              intelectual de nuestro Sitio y Servicios, así como del contenido publicado en ellos.
              Dichos derechos están protegidos por derechos de autor, marcas registradas y demás
              leyes aplicables, además de por tratados internacionales. Usted se compromete a no
              utilizar, copiar ni realizar ninguna acción con respecto a nuestro Sitio y Servicios
              o dicho contenido, salvo que lo permitan expresamente estos Términos. Asimismo,
              deberá respetar los derechos de propiedad intelectual de terceros al ejercer los
              derechos que se le otorgan en virtud del presente documento.
            </p>
          </Section>

          <Section title="4. Uso aceptable">
            <p>
              Usted puede acceder y utilizar nuestros Servicios únicamente para publicar artículos
              en venta o para ver artículos en venta para su posible compra. Queda estrictamente
              prohibido cualquier uso de nuestro Sitio o Servicios para contactar a otros usuarios
              con fines de marketing, venta o promoción de cualquier producto o servicio, que no
              sean artículos en venta, o para acosar a otros usuarios.
            </p>
            <p>
              Si copia, redistribuye o revende cualquiera de nuestros Servicios o cualquier
              contenido disponible a través de nuestro Sitio o Servicios, o si utiliza nuestro
              Sitio o Servicios para contactar a otros usuarios con fines de marketing, venta o
              promoción de cualquier producto o servicio, que no sean artículos en venta, o si
              utiliza nuestro Sitio o Servicios para acosar a otros usuarios de nuestro Sitio o
              Servicios, su derecho de acceso y uso cesará inmediatamente, y deberá eliminar o
              destruir cualquier contenido que haya obtenido en violación de estos Términos.
            </p>
            <p>Usted asume la responsabilidad total por los artículos en venta.</p>
          </Section>

          <Section title="5. Usos prohibidos">
            <p>
              Como parte de nuestro Sitio y Servicios, podemos proporcionar acceso a datos,
              información y contenido relacionados con los Artículos Listados, incluyendo, entre
              otros, los Artículos Listados actuales y históricos y la información, materiales y
              contenido relacionados, así como los números de teléfono y la información de contacto
              de los anunciantes (nuestra &ldquo;Información del Sitio&rdquo;). Usted no debe:
            </p>
            <ul className="list-disc ml-6 space-y-2 marker:text-accent">
              <li>
                modificar o alterar de cualquier forma la información del sitio, el contenido, los
                materiales o las partes de nuestro sitio o servicios;
              </li>
              <li>
                no utilice ninguna información del sitio, ilustraciones, fotografías, secuencias de
                vídeo o audio ni ningún gráfico por separado de ningún texto que lo acompañe, ni
                ningún texto por separado de ninguna ilustración, fotografía, secuencia de vídeo o
                audio ni ningún gráfico que lo acompañe;
              </li>
              <li>
                eliminar cualquier indicación de propiedad de cualquier información o contenido del
                Sitio que provenga de nuestro Sitio o Servicios, o reclamarlo como propio o como
                perteneciente a cualquier otra entidad o individuo;
              </li>
              <li>
                utilizar cualquier parte de nuestro Sitio, Servicios, Información del Sitio o
                contenido en nuestro Sitio o Servicios para cualquier propósito no previsto
                expresamente en la Sección 4 sin obtener una licencia para hacerlo de nosotros o de
                nuestros licenciantes;
              </li>
              <li>
                acceder, o intentar acceder, a partes de nuestro Sitio o Servicios que no están
                destinadas al uso público (incluidas, entre otras, las áreas de administración del
                sitio web);
              </li>
              <li>
                modificar, adaptar o aplicar ingeniería inversa a cualquier aspecto de nuestro
                Sitio, Servicios o Información del Sitio;
              </li>
              <li>
                realizar cualquier tipo de extracción de datos web, recolección de datos web,
                extracción de datos web o cualquier otra extracción de datos de la información del
                sitio o de nuestros sitios o servicios;
              </li>
              <li>
                utilizar cualquier robot, araña, raspador, herramienta de minería de datos,
                herramientas de recopilación de datos, herramientas de extracción de datos o
                cualquier otro medio automatizado para acceder a nuestro Sitio o Servicios, o
                recopilar, copiar o registrar la información o el contenido de nuestro Sitio o
                Servicios;
              </li>
              <li>
                tomar cualquier medida que imponga o pueda imponer (a nuestra entera discreción)
                una carga irrazonable o desproporcionadamente grande en nuestra infraestructura;
              </li>
              <li>
                copiar, descargar, distribuir, transmitir, reutilizar, informar, reproducir,
                modificar, usar, crear obras derivadas de, o mostrar públicamente cualquier
                información del sitio o contenido de nuestro sitio o servicios, incluidos el texto,
                las imágenes, el audio y el video, para fines públicos o comerciales, incluida la
                generación de informes o la agregación de datos o contenido;
              </li>
              <li>extraer informes de la información de nuestro sitio, sitio web o servicios;</li>
              <li>
                tomar fotos, capturas de pantalla u otras imágenes de la información de nuestro
                sitio, del sitio o de los servicios o de cualquier contenido del mismo;
              </li>
              <li>
                utilizar la información de nuestro sitio o nuestro sitio o servicios en competencia
                con nosotros, para desarrollar productos o servicios de la competencia, o de
                cualquier otra manera en nuestro perjuicio o desventaja comercial, o para contactar
                a otros usuarios con el propósito de comercializar, vender o promocionar cualquier
                producto o servicio, que no sea el/los artículo(s) listado(s);
              </li>
              <li>
                utilizar la información de nuestro sitio web o nuestros servicios para realizar
                análisis comparativos o de la competencia con nuestros servicios;
              </li>
              <li>
                identificarnos o mostrar cualquier parte de la información de nuestro sitio, sitio
                o servicios en cualquier sitio web o servicio que nos menosprecie a nosotros o a
                nuestros productos o servicios, o que infrinja cualquiera de nuestros derechos de
                propiedad intelectual u otros derechos;
              </li>
              <li>
                eludir cualquier medida que podamos utilizar para prevenir o restringir el acceso a
                nuestros Servicios, incluidos nuestros encabezados de exclusión de robots;
              </li>
              <li>
                intentar interrumpir o interferir con la Información del Sitio o nuestro Sitio o
                Servicios de cualquier manera o con el uso que otro usuario/visitante haga de
                nuestro Sitio o Servicios, o utilizar la Información del Sitio o nuestro Sitio o
                Servicios como un medio para interrumpir o interferir con otros sitios web o redes;
              </li>
              <li>
                sondear, escanear o probar vulnerabilidades de nuestro Sitio, Servicios, otros
                sitios web o cualquier red conectada a ellos;
              </li>
              <li>
                corromper deliberadamente cualquier información del sitio, datos, documentos o
                contenido disponible a través de nuestro sitio o servicios;
              </li>
              <li>
                transmitir o distribuir un virus en nuestra red y sistemas informáticos de forma
                intencionada o imprudente;
              </li>
              <li>
                utilizar la información de nuestro sitio o nuestro sitio o servicios para acosar a
                otros usuarios de nuestro sitio o servicios;
              </li>
              <li>
                utilizar la información de nuestro sitio o nuestro sitio o servicios de una manera
                que sugiera una asociación no autorizada con cualquiera de nuestros productos,
                sitio, servicios o marcas;
              </li>
              <li>
                utilizar la información de nuestro sitio o nuestro sitio o servicios para enviar
                correos electrónicos, mensajes, llamadas telefónicas o transmisiones prohibidas (es
                decir, spam, correo basura); o
              </li>
              <li>
                ayudar, alentar o permitir que cualquier otra persona realice cualquiera de los
                actos descritos anteriormente.
              </li>
            </ul>
          </Section>

          <Section title="6. Incumplimiento de uso">
            <p>
              Todos los usos prohibidos establecidos en la Sección 5 anterior están prohibidos
              independientemente del medio utilizado, incluyendo, entre otros, el pirateo
              informático o la introducción de gusanos, troyanos, virus, código no autorizado,
              malicioso o dañino, u otro software perjudicial. Al infringir esta disposición, usted
              podría estar cometiendo un delito. Denunciaremos cualquier infracción a las
              autoridades policiales pertinentes y cooperaremos con ellas revelando su identidad.
              En caso de producirse dicha infracción, su derecho a utilizar nuestro Sitio y
              Servicios cesará de inmediato.
            </p>
          </Section>

          <Section title="7. Cargar o publicar contenido en nuestros sitios o servicios">
            <p>
              Recopilaremos los datos, el contenido y la información que usted nos proporcione o
              que recopilemos mientras usted utiliza el Sitio o los Servicios (&ldquo;Contenido del
              Usuario&rdquo;). Por la presente, usted otorga a Vehimax, y declara y garantiza que
              tiene todos los derechos y consentimientos necesarios para otorgar a Vehimax, una
              licencia mundial, perpetua, irrevocable, no exclusiva, transferible, asignable y
              libre de regalías, y todos los permisos y/o consentimientos necesarios (con pleno
              derecho a sublicenciar) para usar, alojar, almacenar, agregar, anonimizar,
              reproducir, explotar, modificar, editar, adaptar, redistribuir, importar, acceder,
              revisar, copiar, compartir, divulgar, generar análisis y resultados de, generar
              informes de, preservar, extraer, publicar, transmitir, mostrar públicamente, ejecutar
              públicamente, comunicar, exhibir, resumir, procesar, traducir, transcribir,
              distribuir, participar en la transferencia de, crear o preparar obras derivadas de,
              poner a disposición de terceros (para su propio uso, almacenamiento, reproducción,
              distribución y derecho a poner a disposición de terceros), y alterar todo su
              Contenido de Usuario de cualquier manera, modo de entrega o medio conocido ahora o
              desarrollado en el futuro por cualquier razón o propósito, incluyendo, entre otros,
              nuestro Sitio y Servicios, desarrollo de productos y servicios, marketing, Análisis,
              garantía de calidad, prevención de fraude, aprendizaje automático, inteligencia
              artificial, capacitación, pruebas y mejora, o cualquier combinación de estos.
            </p>
            <p>
              No nos hacemos responsables de ninguna pérdida o daño a su Contenido de Usuario
              mientras esté en nuestra posesión, y usted debe conservar copias por separado del
              Contenido de Usuario que nos proporcione. Tenemos derecho a revelar su identidad a
              cualquier tercero que alegue que cualquier contenido enviado o subido por usted a
              nuestro Sitio o Servicios constituye una violación de sus derechos de propiedad
              intelectual o de su derecho a la privacidad. En cualquier momento, tenemos derecho a
              eliminar cualquier Contenido de Usuario que usted envíe a nuestro Sitio o Servicios
              si, a nuestra entera discreción, su publicación no cumple con estos Términos.
            </p>
            <p className="font-bold text-foreground">
              NO ASUMIMOS NINGUNA RESPONSABILIDAD NI NINGUNA OBLIGACIÓN POR EL CONTENIDO QUE USTED
              PRODUZCA.
            </p>
            <p>
              Nuestro sitio web y nuestros servicios pueden incluir contenido subido por otros
              usuarios, incluso en foros y salas de chat. Este contenido no ha sido revisado,
              verificado ni aprobado por nosotros. Las opiniones expresadas por otros usuarios en
              nuestro sitio no reflejan nuestras opiniones ni nuestros valores.
            </p>
            <p className="font-semibold uppercase tracking-wide text-foreground">
              Sin limitar de ninguna manera la licencia otorgada a Vehimax en esta Sección 7, usted
              entiende y acepta específicamente que los Artículos Listados que usted coloque en uno
              de nuestros sitios web pueden (a nuestra exclusiva discreción) ser colocados o
              hacerse visibles en otros sitios web proporcionados por Vehimax, sus afiliados o
              socios, incluidos los sitios web nacionales e internacionales listados en{' '}
              <Link to="/our-brands" className="text-accent hover:underline">
                www.vehimax.com/nuestras-marcas
              </Link>
              .
            </p>
          </Section>

          <Section title="8. Sugerencias y propuestas">
            <p>
              Agradecemos y valoramos sus comentarios y opiniones sobre nuestro sitio web y
              servicios. Si nos envía comentarios, preguntas, sugerencias o ideas relacionadas con
              nuestro sitio web y servicios, acepta que dicho contenido no se considera
              confidencial, que no estamos obligados a mantenerlo en secreto y que no estamos
              obligados a responder. Para obtener más información sobre cómo utilizamos dicho
              contenido, consulte nuestra{' '}
              <Link to="/privacy" className="text-accent hover:underline">
                política de privacidad
              </Link>{' '}
              y{' '}
              <Link to="/cookie-policy" className="text-accent hover:underline">
                Política de cookies
              </Link>
              .
            </p>
            <p>
              Tenga en cuenta que si nos envía ideas creativas, sugerencias, invenciones o
              materiales (&ldquo;Ideas Creativas&rdquo;), nosotros:
            </p>
            <ol className="list-[lower-roman] ml-6 space-y-2 marker:text-accent">
              <li>
                seremos propietarios, exclusivamente, de todos los derechos conocidos ahora o que
                se descubran más adelante sobre las Ideas Creativas y cualquier creación, invención
                u obra derivada que resulte de las Ideas Creativas;
              </li>
              <li>
                no estaremos sujetos a ninguna obligación de confidencialidad y no seremos
                responsables por ningún uso o divulgación de ninguna Idea Creativa; y
              </li>
              <li>
                tendremos derecho a usar sin restricciones las Ideas Creativas para cualquier
                propósito que sea, comercial o de otro tipo, sin compensación para usted ni para
                ninguna otra persona.
              </li>
            </ol>
          </Section>

          <Section title="9. Cumplimiento de las leyes">
            <p>
              Usted es responsable de, y acepta cumplir, todas las leyes, normas y reglamentos
              locales, estatales, federales e internacionales aplicables a su uso de nuestro Sitio
              y Servicios, y cualquier otra herramienta, servicio o producto ofrecido en nuestro
              Sitio y Servicios y cualquier transacción que pueda realizar con otros usuarios en
              relación con el uso de nuestro Sitio o Servicios. Los usuarios deben cumplir con las
              leyes, reglamentos, normas y órdenes reguladoras del país local, incluyendo la Ley de
              Prácticas Corruptas en el Extranjero (FCPA) y la Ley de Control de Exportaciones de
              los Estados Unidos (ECPA), además de las leyes locales aplicables.
            </p>
            <p>
              No podrá utilizar nuestros Servicios si reside en un país sujeto a embargo por parte
              de los Estados Unidos, o si es una persona o entidad extranjera bloqueada o vetada
              por el gobierno de los Estados Unidos o en virtud de leyes o reglamentos similares
              aplicables en otras jurisdicciones. Los artículos listados pueden estar sujetos a
              leyes, reglamentos, prohibiciones, restricciones o costos de exportación,
              reexportación e importación. Usted es el único responsable de identificar y cumplir
              con las leyes, normas, reglamentos, licencias y/o requisitos aduaneros aplicables a
              los artículos listados y a cualquier transacción que realice con otros usuarios. Si
              un artículo listado es incautado o destruido por alguna agencia aduanera
              gubernamental o es devuelto, no nos haremos responsables de las pérdidas, del
              artículo listado ni de los reembolsos de envío.
            </p>
            <p>
              Tenga en cuenta que, si bien no participamos en ninguna transacción entre usuarios y
              no asumimos ninguna responsabilidad por el cumplimiento legal o normativo relacionado
              con los artículos publicados en nuestro sitio o servicios, puede haber circunstancias
              en las que estemos legalmente obligados (según lo determinemos a nuestra entera
              discreción) a proporcionar información relativa a nuestros servicios y artículos
              publicados para cumplir con las solicitudes de organismos gubernamentales en relación
              con investigaciones, litigios o procedimientos administrativos, y podemos optar por
              cumplir con dichas obligaciones a nuestra entera discreción.
            </p>
          </Section>

          <Section title="10. Impuestos">
            <p>
              No nos hacemos responsables de determinar si se aplican impuestos a ninguna
              transacción entre usuarios, ni de retener, recaudar, declarar o remitir ningún
              impuesto derivado de los artículos anunciados. No ofrecemos asesoramiento fiscal, y
              nada de lo que digamos o hagamos debe interpretarse como tal, incluyendo la
              oportunidad que le brindamos de conocer y utilizar por separado servicios o
              soluciones relacionados con impuestos (por ejemplo, software automatizado de
              cumplimiento tributario). El pago y la recaudación de los impuestos aplicables
              (incluidos el impuesto sobre la renta, el impuesto sobre la propiedad, el impuesto
              sobre las ventas, el impuesto especial, el impuesto sobre la importación, el
              impuesto sobre la exportación, el IVA y otros impuestos o aranceles asociados con la
              compra/uso y venta/promoción de cualquier artículo anunciado) son responsabilidad
              exclusiva del comprador y del vendedor, según corresponda. En general, es
              responsabilidad del vendedor determinar qué impuestos, si los hubiere, se aplican a
              una transacción y recaudar, declarar y/o remitir el impuesto correcto a la autoridad
              tributaria correspondiente; sin embargo, si tiene alguna pregunta sobre sus propias
              obligaciones de pago, recaudación, declaración o remisión, debe consultar a un
              asesor fiscal cualificado.
            </p>
          </Section>

          <Section title="11. Financiación, envío, seguros y otros servicios">
            <p>
              Podemos proporcionar enlaces en nuestro Sitio o Servicios a otros sitios web operados
              por nosotros, proveedores externos o afiliados que ofrecen diversos servicios u otras
              ofertas, incluyendo, sin limitación, servicios de financiamiento, envío y seguros,
              herramientas de precios y otras ofertas que podrían ser de su interés, y solo para la
              comodidad de nuestros usuarios. Además, usted acepta que todas las comunicaciones y
              llamadas telefónicas que Vehimax reciba, procese o reenvíe desde, hacia o entre
              usted, otros usuarios, vendedores y/o compradores a través de nuestro sistema de
              telecomunicaciones, nuestro Sitio o nuestros Servicios puedan ser grabadas,
              transcritas, resumidas, analizadas, monitoreadas y/o almacenadas por Vehimax
              mediante cualquier método o tecnología, incluyendo herramientas de inteligencia
              artificial (&ldquo;IA&rdquo;), para proporcionarnos clientes potenciales de
              marketing/ventas a nosotros, a nuestros afiliados o a empresas de terceros que
              ofrecen diversos servicios u otras ofertas.
            </p>
            <p>
              Como contraprestación por proporcionar dichos enlaces o contactos de marketing/ventas,
              podríamos recibir una comisión de los terceros o afiliados cuyos productos o
              servicios usted decida adquirir. No nos hacemos responsables de los actos u omisiones
              de dichos terceros o afiliados, cuyo uso usted realiza bajo su propia responsabilidad.
              El acceso y uso de nuestros otros sitios web y de los servicios y sitios web de
              terceros o afiliados puede estar sujeto a los términos y condiciones aplicables de
              cada sitio web, tercero o afiliado.
            </p>
          </Section>

          <Section title="12. Descargo de responsabilidad; Sin garantía">
            <p className="uppercase tracking-wide font-semibold text-foreground">
              En la medida máxima permitida por la ley, nuestro Sitio y Servicios, incluyendo todo
              el contenido, software, funciones, materiales e información disponibles o accesibles
              a través de nuestro Sitio o Servicios, se proporcionan &ldquo;tal cual&rdquo; y
              &ldquo;según disponibilidad&rdquo;, sin garantías de ningún tipo, ni expresas ni
              implícitas, incluyendo, sin limitación, las garantías implícitas de no infracción,
              comerciabilidad o idoneidad para un fin particular. Somos únicamente un intermediario
              pasivo para proporcionar contenido y facilitar la comunicación entre un comprador y
              un vendedor, y en ningún caso vendemos ningún artículo listado. No garantizamos que
              los artículos listados funcionen correctamente al recibirlos, ni que continúen
              funcionando durante un periodo de tiempo posterior a su recepción.
            </p>
            <p className="uppercase tracking-wide font-semibold text-foreground">
              En la medida máxima permitida por la ley, no ofrecemos declaraciones ni garantías de
              ningún tipo respecto a los productos o servicios, ni respecto a la seguridad asociada
              con la transmisión de información confidencial a través de nuestro Sitio, Servicios o
              cualquier sitio enlazado, incluso si tenemos conocimiento de algún problema al
              respecto. No garantizamos que las funciones contenidas en nuestro Sitio o Servicios,
              ni ningún material o contenido contenido en ellos, sean precisos, completos,
              actualizados, ininterrumpidos o libres de errores, que se corrijan los defectos, ni
              que nuestros Servicios o el Sitio o los servidores que los hacen disponibles estén
              libres de virus u otros componentes dañinos.
            </p>
            <p className="uppercase tracking-wide font-semibold text-foreground">
              Usted reconoce y acepta que cualquier transmisión, llamada telefónica o comunicación
              hacia y desde nuestro Sitio y Servicios no es confidencial y sus llamadas telefónicas,
              comunicaciones o contenido aportado pueden ser leídos, grabados, transcritos,
              resumidos, analizados, monitoreados, transferidos, reenviados, almacenados o
              interceptados por otros o por nosotros a través de cualquier método o tecnología,
              incluidas las herramientas de inteligencia artificial (&ldquo;IA&rdquo;). Usted
              reconoce y acepta además que al realizar o enviar llamadas telefónicas, comunicaciones
              o contenido a nosotros o a través de nuestro Sitio o Servicios y/o al publicar
              información en nuestros Sitios o Servicios, incluidos los Artículos Listados, no se
              crea ninguna relación confidencial, fiduciaria, contractual implícita o de otro tipo
              entre usted y nosotros que no sea la establecida en estos Términos.
            </p>
            <p className="uppercase tracking-wide font-semibold text-foreground">
              Usted reconoce y acepta que no nos responsabilizará ni intentará responsabilizarnos
              por el contenido proporcionado por ningún usuario, incluyendo, sin limitación,
              cualquier traducción del mismo, y además reconoce y acepta que no somos parte de
              ninguna transacción de Artículos Listados ni de ninguna otra transacción entre
              usuarios de nuestros Sitios o Servicios. No tenemos control ni garantizamos la
              seguridad de ninguna transacción, Artículo Listado ni la veracidad o exactitud de
              ningún Artículo Listado u otro contenido proporcionado en nuestro Sitio y Servicios.
            </p>
          </Section>

          <Section title="13. Indemnización">
            <p>
              Usted indemnizará, defenderá y mantendrá indemne a Vehimax, sus subsidiarias,
              afiliadas y a cada uno de sus respectivos funcionarios, directores, empleados,
              agentes, sucesores y cesionarios (las &ldquo;Partes Indemnizadas&rdquo;) frente a
              cualquier reclamación o demanda (incluidos los honorarios y costos razonables de
              abogados y peritos) presentada por cualquier tercero debido a o derivada de:
            </p>
            <ol className="list-[lower-roman] ml-6 space-y-2 marker:text-accent">
              <li>su incumplimiento de estos Términos;</li>
              <li>su uso indebido de nuestro Sitio o Servicios;</li>
              <li>
                cualquier lesión personal, muerte o daño a la propiedad causado por o derivado de
                la publicación, venta, transporte o uso de cualquier Artículo(s) Listado(s); o
              </li>
              <li>
                su violación de cualquier ley o derecho de un tercero, incluyendo lo relacionado
                con cualquier Contenido de Usuario que usted proporcione o publique.
              </li>
            </ol>
            <p>
              Nos reservamos el derecho, a nuestro cargo, de asumir la defensa y el control
              exclusivos de cualquier asunto sujeto a indemnización por su parte, y usted no deberá
              en ningún caso llegar a un acuerdo sobre ningún asunto sin nuestro consentimiento por
              escrito. Si bien algunas jurisdicciones pueden limitar su responsabilidad en virtud
              del presente, esta indemnización se aplicará en la máxima medida permitida por la ley
              aplicable.
            </p>
          </Section>

          <Section title="14. Liberar">
            <p className="uppercase tracking-wide font-semibold text-foreground">
              En caso de que usted tenga una disputa con uno o más usuarios de nuestro Sitio o
              Servicios, incluyendo, sin limitación, cualquier tercero visitante del Sitio o el
              proveedor de cualquier sitio web o aplicación de terceros que pueda estar vinculado a
              o desde nuestro Sitio o Servicios, usted por la presente acepta liberar, remitir y
              exonerar para siempre a las Partes Indemnizadas de cualquier y todo tipo de derechos,
              reclamaciones, quejas, demandas, causas de acción, procedimientos, responsabilidades,
              obligaciones, honorarios legales, costos y desembolsos de cualquier naturaleza, ya
              sean conocidos o desconocidos, que ahora o en el futuro surjan de, se relacionen con
              o estén conectados con dicha disputa.
            </p>
          </Section>

          <Section title="15. Limitación de responsabilidad">
            <p className="uppercase tracking-wide font-semibold text-foreground">
              En la medida máxima permitida por la ley, en ningún caso las Partes Indemnizadas
              serán responsables por daños indirectos, consecuentes, especiales, incidentales o
              punitivos, ya sea por contrato o agravio o bajo cualquier otra teoría de
              responsabilidad, incluyendo sin limitación, pérdida de beneficios, beneficios
              anticipados, negocios o ventas, cualquier pérdida de buena voluntad o reputación, o
              los costos de bienes o servicios sustitutos, incluso si las Partes Indemnizadas, o un
              representante autorizado de las mismas, han sido advertidos de la posibilidad de
              dichos daños, que surjan de, se basen en o resulten de:
            </p>
            <ol className="list-[lower-alpha] ml-6 space-y-2 marker:text-accent">
              <li>nuestros Servicios;</li>
              <li>nuestro Sitio;</li>
              <li>estos Términos;</li>
              <li>cualquier incumplimiento de estos Términos por parte de usted o de un tercero;</li>
              <li>
                el uso que usted o cualquier tercero haga de nuestro Sitio o Servicios, o de los
                servicios que cualquier tercero proporcione, relacionado con el negocio que
                operamos;
              </li>
              <li>cualquier información del usuario o contenido aportado por el usuario;</li>
              <li>
                cualquier interacción entre nuestro Sitio o Servicios y cualquier sitio de terceros;
                y
              </li>
              <li>
                cualquier comunicación, llamada telefónica o transacción real o intentada,
                incluyendo sin limitación, cualquier pago o transacción de compra (incluso si
                nosotros o cualquier tercero recibimos una tarifa en relación con ella) entre los
                usuarios de nuestro Sitio o Servicios.
              </li>
            </ol>
            <p className="uppercase tracking-wide font-semibold text-foreground">
              En ningún caso la responsabilidad total de las Partes Indemnizadas hacia usted por
              todos los daños, pérdidas y causas de acción (ya sea por contrato o agravio,
              incluyendo, pero sin limitarse a, negligencia, responsabilidad por productos o de
              otro tipo) que surjan de estos Términos, la compra, venta o uso de cualquier Artículo
              Listado, o su uso de nuestro Sitio o Servicios excederá, en conjunto, la menor de
              mil dólares (USD 1.000) o las honorarios totales ganados por nosotros en relación
              con el/los Artículo(s) Listado(s) a los que se relaciona la reclamación aplicable.
            </p>
          </Section>

          <Section title="16. Grabación, transcripción y monitorización de llamadas telefónicas y comunicaciones">
            <p>
              Usted acepta y consiente que todas las comunicaciones y llamadas telefónicas que
              Vehimax reciba, procese o reenvíe desde, hacia o entre usted, otros usuarios,
              vendedores y/o compradores a través de nuestro sistema de telecomunicaciones,
              nuestro Sitio o nuestros Servicios puedan ser grabadas, transcritas, resumidas,
              analizadas, monitoreadas y/o almacenadas por Vehimax a través de cualquier método o
              tecnología, incluidas herramientas de inteligencia artificial (&ldquo;IA&rdquo;),
              para el control de calidad; para proporcionarnos clientes potenciales de
              marketing/ventas a nosotros, a nuestros afiliados o a empresas de terceros; para
              brindarle Servicios; y para el cumplimiento de estos Términos.
            </p>
            <p>
              Las leyes relativas a los requisitos de notificación y aviso para grabar
              conversaciones individuales y/o cargar conversaciones grabadas varían según la
              ubicación. Si utiliza los Servicios de Vehimax para grabar, monitorear y/o
              transcribir llamadas telefónicas (las &ldquo;Herramientas de estadísticas de
              llamadas&rdquo;) en relación con sus Artículos listados o negocio, usted:
            </p>
            <ol className="list-[lower-roman] ml-6 space-y-2 marker:text-accent">
              <li>
                reconoce y acepta que es el único responsable de proporcionar cualquier aviso y
                obtener el consentimiento de las personas en relación con cualquier grabación según
                lo exija la ley aplicable, incluidos, sin limitación, sus empleados, agentes,
                contratistas u otros usuarios (colectivamente, &ldquo;Sus Usuarios&rdquo;);
              </li>
              <li>
                reconoce y acepta que es el único responsable de administrar la configuración y su
                uso de las Herramientas de estadísticas de llamadas, incluido si debe incluir una
                notificación de &ldquo;llamada telefónica grabada&rdquo; para las llamadas
                recibidas por usted o sus Usuarios;
              </li>
              <li>
                por la presente declara y garantiza que sus selecciones de configuración y el uso
                de dichas Herramientas de estadísticas de llamadas por parte de usted o sus
                Usuarios cumplen con toda la ley aplicable;
              </li>
              <li>
                reconoce y acepta que es el único responsable de su uso de todas las grabaciones y
                transcripciones de grabaciones; y
              </li>
              <li>
                declara y garantiza que ha obtenido todos los consentimientos y permisos necesarios
                y apropiados de sus Usuarios para ser grabados, monitoreados y/o transcritos por o
                a través de las Herramientas de estadísticas de llamadas.
              </li>
            </ol>
            <p>
              Asimismo, usted acepta indemnizar, defender y eximir de responsabilidad a Vehimax,
              sus subsidiarias, afiliadas y a cada uno de sus respectivos funcionarios, directores,
              empleados, agentes, sucesores y cesionarios frente a cualquier reclamación o demanda
              (incluidos los honorarios y costos razonables de abogados y expertos) presentada por
              cualquier tercero debido a, o que surja de, su uso de grabaciones de llamadas,
              transcripciones de llamadas, resúmenes de llamadas, datos de llamadas y las
              Herramientas de estadísticas de llamadas de cualquier manera.
            </p>
          </Section>

          <Section title="17. General">
            <Subsection title="Ley aplicable">
              Estos Términos se rigen e interpretan de conformidad con las leyes del País, sin
              tener en cuenta sus disposiciones sobre conflicto de leyes. Usted acepta
              irrevocablemente la jurisdicción exclusiva de los tribunales del Condado de Hendry,
              Florida, EE. UU., para cualquier disputa que surja de estos Términos.
            </Subsection>

            <Subsection title="Traducciones">
              Para todos los efectos, la versión en inglés de estos Términos será el documento
              original que regirá el acuerdo entre las partes. En caso de conflicto entre la
              versión en inglés de estos Términos y cualquier traducción posterior a otro idioma,
              prevalecerá la versión en inglés.
            </Subsection>

            <Subsection title="Enmiendas">
              Nos reservamos el derecho de modificar, añadir o eliminar cualquier parte de estos
              Términos (incluidos los documentos incorporados al presente documento), a nuestra
              entera discreción, con o sin previo aviso, mediante la publicación de dichos cambios
              en nuestro Sitio web y/o Servicios. Una vez publicados, dichos cambios entrarán en
              vigor.
            </Subsection>

            <Subsection title="Términos completos">
              El párrafo introductorio y cualquier documento vinculado o referenciado en estos
              Términos se incorporan por referencia y forman parte integrante de los mismos. En
              conjunto, lo anterior constituye el acuerdo completo entre las partes y reemplaza
              todos los acuerdos y entendimientos previos, ya sean escritos u orales, relacionados
              con el objeto de estos Términos.
            </Subsection>

            <Subsection title="Consejo">
              Cualquier orientación, comentario o consejo proporcionado por Vehimax, sus empleados,
              contratistas independientes y agentes en relación con el uso de nuestro Sitio y
              Servicios, incluyendo, entre otros, la orientación para publicar o revisar artículos,
              tiene únicamente fines informativos generales. No constituye asesoramiento legal, y
              usted acepta evaluar de forma independiente dicha información y no basarse en ella
              para ningún propósito.
            </Subsection>

            <Subsection title="Encabezados">
              Los títulos utilizados en este acuerdo tienen como único fin facilitar la lectura y
              no tendrán ninguna fuerza ni efecto en la interpretación del mismo.
            </Subsection>

            <Subsection title="Reforma">
              Si un tribunal de jurisdicción competente determina que el alcance de alguna
              disposición de estos Términos es demasiado amplio para ser aplicado, el tribunal
              modificará dichas disposiciones para que tengan un alcance más limitado que considere
              aplicable.
            </Subsection>

            <Subsection title="Supervivencia">
              Las disposiciones de estos Términos que, por su naturaleza, deban aplicarse más allá
              de su vigencia, permanecerán en vigor después de cualquier terminación o vencimiento
              de estos Términos, incluyendo, entre otras, las Secciones 3, 7, 8, 12, 13, 14, 15, 16
              y 17.
            </Subsection>

            <Subsection title="Divisibilidad">
              Si alguna disposición de estos Términos se considera inaplicable por cualquier motivo
              y no puede ser reformada por un tribunal de jurisdicción competente según lo
              dispuesto anteriormente, dicha disposición se considerará separada y divisible, y la
              inaplicabilidad de dicha disposición no invalidará ni hará inaplicables las demás
              disposiciones del presente documento.
            </Subsection>

            <Subsection title="Sucesores y cesionarios">
              Estos Términos serán vinculantes para ambas partes y sus respectivos sucesores y
              cesionarios autorizados, y redundarán en su beneficio. Usted no podrá ceder estos
              Términos sin nuestro consentimiento previo por escrito. Nosotros podremos ceder
              libremente estos Términos a cualquier tercero.
            </Subsection>

            <Subsection title="Exención">
              Ningún retraso u omisión por parte de una de las partes en el ejercicio de cualquier
              derecho conforme a estos Términos se interpretará como una renuncia a dicho derecho
              ni a ningún otro. La renuncia o el consentimiento otorgado por una de las partes en
              una ocasión determinada sólo será efectivo en ese caso y no se interpretará como una
              limitación o renuncia a ningún derecho en ninguna otra ocasión.
            </Subsection>

            <Subsection title="Disponibilidad internacional">
              No garantizamos que nuestro sitio web y servicios estén disponibles en todo el mundo,
              ni que las transacciones de compraventa entre usuarios puedan realizarse en cualquier
              lugar del planeta. Usted se compromete a cumplir con todas las normas locales
              relativas a la conducta en línea y el contenido aceptable, incluidas, entre otras,
              las leyes aplicables a la transmisión de datos exportados desde Estados Unidos al
              país donde reside. Tenga en cuenta que podrían aplicarse términos adicionales o
              diferentes si reside fuera de Estados Unidos. Para obtener más información, haga clic
              en el enlace de ubicación que aparece a continuación.
            </Subsection>
          </Section>

          <Section title="Otros términos">
            <p>
              Nuestra{' '}
              <Link to="/privacy" className="text-accent hover:underline">
                política de privacidad
              </Link>{' '}
              establece los términos en los que procesamos cualquier dato personal que recopilemos
              de usted o que usted nos proporcione.
            </p>
            <p>
              <Link to="/cookie-policy" className="text-accent hover:underline">
                Política de cookies
              </Link>
              <br />
              Aquí se expone nuestra política en relación con las cookies.
            </p>
            <p>
              Somos uno de los{' '}
              <Link to="/our-group" className="text-accent hover:underline">
                Vehimax Grupo de empresas
              </Link>
              .
            </p>
          </Section>

          <p className="text-sm text-muted-foreground italic mt-12">
            Última actualización: 9 de Junio de 2026
          </p>
        </article>
      </div>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-primary mb-4 pb-2 border-b border-border">
        {title}
      </h2>
      <div className="space-y-4 text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}


function Subsection({ title, children }) {
  return (
    <div className="mt-5">
      <h3 className="font-semibold text-foreground uppercase tracking-wide text-sm mb-2">{title}</h3>
      <p>{children}</p>
    </div>
  );
}
